import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Stack, Heading, Text, Flex, Skeleton, useToast } from '@chakra-ui/react'
import { useMediaQuery } from 'react-responsive'

import WebBeacon from 'components/common/webBeacon'
import { IDashboardBanks, IDashboardCollaborator, IGetDashborad } from 'services/IApi.d'

import BanksAdditional from './SubscriptionResumenComponents/BanksAdditional/BanksAdditional'
import ErrorPage from '../ErrorPage'
import { getFileData, getFileTermsConditionData, getResumenStatus, confirmData, getDocumentsDownloadData } from './SubscriptionResumenData'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import HeaderOffRoad from 'views/Layout/headerOffRoad/HeaderOffRoad'
import bank from 'assets/images/bank.svg'
import admins from 'assets/images/admins.svg'
import repre from 'assets/images/representative.svg'
import PersonAdministrator from './SubscriptionResumenComponents/PersonAdministrator/PersonAdministrator'
import PersonRepresentative from './SubscriptionResumenComponents/PersonRepresentative/PersonRepresentative';
import Title from './SubscriptionResumenComponents/Title/Title';
import Company from './SubscriptionResumenComponents/Company/Company';
import Price from './SubscriptionResumenComponents/Price/Price';
import FooterLabel from './SubscriptionResumenComponents/FooterLabel/FooterLabel';
import FooterBottomNav from './SubscriptionResumenComponents/BottomNav/BottomNav';
import { SUBSCRIPTION_TYPE } from 'utils/subscriptionConst';
import { COLLABORATOR_ROLE } from 'utils/collaborationConst';
import TooltipTyC from './SubscriptionResumenComponents/TooltipTyC/TooltipTyC';
import { ToastComponent } from '@wow/toast';
import { Divider } from '@wow/divider';

const SubscriptionResumen = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' })
  const webBeaconSrc = process.env.REACT_APP_WEB_BEACON_IMAGE
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { estado } = Object.fromEntries([...searchParams])
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [error, setError] = useState('')
  const [isLoadingScreen, setIsLoadingScreen] = useState<boolean>(true)
  const [isLoadingConfirm, setIsLoadingConfirm] = useState<boolean>(false)
  const [isCheckedTyC, setIsCheckedTyC] = useState<boolean>(false)
  const [resumen, setResumen] = useState<IGetDashborad>(null)
  const [initialized, setInitialized] = useState<boolean>(false);
  const { company, plan, collaborators, subscriptionType } = resumen || {}

  const [idEmpresa] = useState(localStorage.getItem('idEmpresa'))
  const [idSuscriptor] = useState(localStorage.getItem('idSuscriptor'))

  const banks: IDashboardBanks[] = plan?.banks || []

  const suscriptor = collaborators?.find((collab: IDashboardCollaborator) => collab.isSubscriptor)
  const administrators = collaborators?.filter(data => data.role.includes(COLLABORATOR_ROLE.administrator) ) || []
  const representatives = collaborators?.filter(data => data.role.includes(COLLABORATOR_ROLE.legal_representative) ) || []
  const hasBankPhysical = (subscriptionType === SUBSCRIPTION_TYPE.mix || subscriptionType === SUBSCRIPTION_TYPE.physical)

  const titulo = `${suscriptor?.name} ${suscriptor?.lastName}, con el rol de administrador general en Interbanking, solicitó nuestro servicio para ${company?.name} asignándote como representante legal o persona apoderado.`

  const titleError = 'En estos momentos presentamos inconvenientes en la plataforma,'
  const subtitleError = 'Por favor intentá nuevamente en unos minutos'

  const countAdministratorsLabel = administrators.length === 1 ? `${administrators.length} administrador`: `${administrators.length} administradores`
  const countRepresentativesLabel = representatives.length === 1 ? `${representatives.length} representante` : `${representatives.length} representantes`

  const toast = useToast()
  const openToast = () => {
    toast({
      duration: 7000,
      position: 'bottom-left',
      status: 'error',
      description: 'No podemos confirmar la solicitud, volvé a intentarlo.',
      isClosable: false,
      containerStyle: { 
        marginLeft: isMobile ? '-10px':'-18px', 
        width: isMobile ? '348px':'480px', 
        transform: isMobile ? 'translateY(-190%)':'translateY(-90%)', 
        textStyle: 'bodyMd'},
      render: props => (
        <ToastComponent {...props} />
      ),
    })
  }

  
  const handleChecked = useCallback(async function checked (e: boolean) {
    setIsCheckedTyC(e)
  }, [])

  const handleTermsCondition = useCallback(async function termsCondition () {
    (hasBankPhysical) ? getDocumentsDownloadData().catch(error => console.error('Error al descargar los términos y condiciones:', error))
      : getFileTermsConditionData(executeRecaptcha).catch(error => console.error('Error al descargar los términos y condiciones:', error)) 
  }, [executeRecaptcha, hasBankPhysical])

  const handleFile = useCallback(async function getFile () {
    getFileData(executeRecaptcha).catch(error => console.error('Error al descargar archivo:', error))
  }, [executeRecaptcha])
  

  const handleConfirm = useCallback(async function confirm () {
    try {
      setIsLoadingConfirm(true)
      const res = await confirmData(executeRecaptcha)
      if (res) {
        navigate('/exito', { state: { hasBankPhysical } }) 
      } else {
        openToast()
      }  
      setIsLoadingConfirm(false)    
    } catch (error) {
      console.error('Error al confirmar:', error)
    }    
  }, [executeRecaptcha, hasBankPhysical])


  useEffect(() => {
    if (estado !== 'VALIDADO') {
      setError('error')
    }
    if (resumen) { return }
    getResumenStatus(idSuscriptor, idEmpresa).then(setResumen)
      .catch(() => {setError('error')})
      .finally(() => {
        setIsLoadingScreen(false)
        setInitialized(true)
      })
  }, [idSuscriptor, idEmpresa])

  
  return (
    error ? <ErrorPage title={titleError} subtitle={subtitleError} /> :
    <>
    <HeaderOffRoad />
    <Stack data-testid='subscriptionResume-body' display='Flex' margin='auto'  position='relative' alignItems='center' maxW={['360px', '992px']} >
      <Box maxW={['360px', '600px']}  margin='auto' mb={['200px', '100px']} mt='14px'  >
        <WebBeacon src={webBeaconSrc} id='id-web-beacon' />
        <Stack data-testid='subscriptionResume-body-data' display='Flex' margin='auto' gap={'10px'} position='relative' alignItems='center'>
          <Title titulo={titulo || ''} />

          <Box  bg="white" borderRadius={12} p='8px 40px 8px 40px' gap='12px'>
            <Stack  maxW={['360px', '520px' ]}  gap='12px'>
              <Skeleton isLoaded={!isLoadingScreen}>
                <Company  company={company} />
                <Price  isLoadingScreen={isLoadingScreen} plan={plan} />
              </Skeleton>
            </Stack>

            <Stack maxW={['360px', '520px']}  borderBottomWidth='1px' borderBottomStyle='solid' borderBottomColor='secondary.100' gap='16px' pb='16px'>
              <Skeleton isLoaded={!isLoadingScreen}>
                <Heading  pt='12px' gap='8px'>
                  <Text data-testid='subscriptionResume-bankTitle' textStyle='overlineLg' fontWeight='600' size='4xs' line-height='xs' letterSpacing='1.2px' color='secondary.500'>PARA OPERAR CON:</Text>
                </Heading>
                <Stack  pt='12px' gap='16px'>                    
                  <Stack maxW={['360px', '520px' ]}>
                    <Flex data-testid='subscriptionResume-banks' flexDir='row' gap='8px' align='center'>
                      <img src={bank} alt="" width='20px' height='20px' />
                      <Text data-testid='subscriptionResume-bankCount' textStyle='labelXl' fontWeight='400' size='xs' line-height='md' color='secondary.900'>{banks.length} bancos</Text>
                    </Flex>
                  </Stack>
                  <BanksAdditional banks={banks} />
                </Stack>
              </Skeleton>              

              <Divider orientation="horizontal"  variant="light" />

              <Stack mt='18px' pb='16px' >
                <Skeleton isLoaded={!isLoadingScreen}>
                  <Flex data-testid='subscriptionResume-administrators' flexDir='row' gap='8px' align='center'>
                    <img src={admins} alt="admins"  width='20px' height='20px' />                
                    <Text data-testid='subscriptionResume-administratorCount' textStyle='labelXl' fontWeight='400' size='xs' line-height='md' color='secondary.900'>{countAdministratorsLabel}</Text>
                  </Flex>
                  <PersonAdministrator person={administrators} />
                </Skeleton>
              </Stack>

              {representatives.length > 0 &&
                <>
                <Divider orientation="horizontal"  variant="light" />

                <Stack mt='18px' pb='16px' >
                  <Skeleton isLoaded={!isLoadingScreen}>
                    <Flex data-testid='subscriptionResume-collaborators' flexDir='row' gap='8px' align='center'>
                      <img src={repre} alt="representante" width='20px' height='20px' />                
                      <Text data-testid='subscriptionResume-collaboratorCount' textStyle='labelXl' fontWeight='400' size='xs' line-height='md' color='secondary.900'>{countRepresentativesLabel}</Text>
                    </Flex>
                    <PersonRepresentative  person={representatives} />
                  </Skeleton>
                </Stack>
                </>
              }
            </Stack>

            <Stack maxW={['360px', '520px' ]} display='flex' p='30px 0 0' justifyContent='center' alignItems='center'>
              <FooterLabel  isLoadingScreen={isLoadingScreen} />
            </Stack>
          </Box>
        </Stack>        
      </Box>    
    </Stack>
    <FooterBottomNav  isChecked = {isCheckedTyC}  isLoadingConfirm = {isLoadingConfirm}
        onAction = {handleConfirm} 
        onTermCondition = {handleTermsCondition} 
        onGetFile = {handleFile} 
        onChecked = {handleChecked}
    />
    { initialized &&
    <TooltipTyC initialized={initialized && !isCheckedTyC}  setInitialized={setInitialized}  />
    }
  </>
)}

export default SubscriptionResumen
